import type { BonusCampaign, Marketplace } from '../@types/api';
import { dayjs } from '../config/dayjs';
import { CASHBACK_BONUS_PRODUCT_TYPE_OPTIONS, CASHBACK_BONUS_REWARD_TYPE_OPTIONS } from '../constants';
import { CAMPAIGN_REWARD_TYPE, VALIDITY_PERIOD } from '../constants/enums';
import type { CashbackBonusFormData, CashbackBonusFormValues } from '../schema';

type DirtyCashbackBonusFields = Partial<Record<keyof CashbackBonusFormValues, unknown>>;

type MarketplaceProducts = Partial<Marketplace['marketplaceConfig']['products']>;

export const getCashbackBonusProductOptions = (
  isCasinoCashbackEnabled: boolean,
  existingProduct?: BonusCampaign['product'],
  products?: MarketplaceProducts
) =>
  CASHBACK_BONUS_PRODUCT_TYPE_OPTIONS.filter((option) => {
    if (option.id === existingProduct) {
      return true;
    }

    if (option.id === 'SPORTSBOOK') {
      return products?.sportsbook !== false;
    }

    return products?.casino === true && isCasinoCashbackEnabled;
  });

export const getCashbackBonusDefaultProduct = (
  isCasinoCashbackEnabled: boolean,
  products?: MarketplaceProducts
): CashbackBonusFormValues['product'] =>
  products?.casino === true && isCasinoCashbackEnabled ? 'CASINO' : 'SPORTSBOOK';

export const getCashbackRewardTypeOptions = (
  isFreeSpinsEnabled: boolean,
  product: CashbackBonusFormValues['product'],
  existingRewardType?: BonusCampaign['rewardType']
) => {
  const supportsFreeSpins =
    product === 'CASINO' && (isFreeSpinsEnabled || existingRewardType === CAMPAIGN_REWARD_TYPE.FREE_SPINS);

  return supportsFreeSpins
    ? CASHBACK_BONUS_REWARD_TYPE_OPTIONS
    : CASHBACK_BONUS_REWARD_TYPE_OPTIONS.filter((option) => option.id === CAMPAIGN_REWARD_TYPE.REAL_MONEY);
};

export const buildCashbackBonusEditFormValues = (item: BonusCampaign): CashbackBonusFormValues => ({
  ...item,
  description: item.description || '',
  fromTimestamp: item.effectiveDateStart ? dayjs(item.effectiveDateStart) : undefined,
  toTimestamp: item.effectiveDateEnd ? dayjs(item.effectiveDateEnd) : undefined,
  validityPeriod: item.effectiveDateStart || item.effectiveDateEnd ? VALIDITY_PERIOD.CUSTOM : VALIDITY_PERIOD.ONGOING,
  ...(item.rewardType === CAMPAIGN_REWARD_TYPE.FREE_SPINS
    ? { rewardType: CAMPAIGN_REWARD_TYPE.FREE_SPINS, product: 'CASINO' as const }
    : { rewardType: CAMPAIGN_REWARD_TYPE.REAL_MONEY }),
  refundPercentage: item.refundPercentage ?? 1,
  minimumLostAmount: item.minimumLostAmount ?? 0,
  maximumRefundAmount: item.maximumRefundAmount || 0,
  freeSpinTiers: (item.freeSpinTiers ?? []).map((tier) => ({
    ...tier,
    aggregator: tier.aggregator ?? 'GGA',
    gameId: String(tier.gameId),
  })),
  userIds: item.userIds || [],
});

export const buildCashbackBonusCreatePayload = (data: CashbackBonusFormData) => {
  const {
    description,
    eligibility,
    fromTimestamp,
    name,
    product,
    rewardType,
    schedule,
    status,
    toTimestamp,
    userIds,
    validityPeriod,
  } = data;
  const commonPayload = {
    description,
    eligibility,
    fromTimestamp: fromTimestamp?.valueOf(),
    name,
    product,
    rewardType,
    schedule,
    status,
    toTimestamp: toTimestamp?.valueOf(),
    userIds,
    validityPeriod,
  };

  if (data.rewardType === CAMPAIGN_REWARD_TYPE.FREE_SPINS) {
    return {
      ...commonPayload,
      freeSpinTiers: data.freeSpinTiers.map((tier) => ({
        ...tier,
        gameId: Number(tier.gameId),
      })),
    };
  }

  return {
    ...commonPayload,
    refundPercentage: data.refundPercentage,
    minimumLostAmount: data.minimumLostAmount,
    maximumRefundAmount: data.maximumRefundAmount || undefined,
  };
};

export const buildCashbackBonusUpdatePayload = (
  data: CashbackBonusFormData,
  dirtyFields: DirtyCashbackBonusFields
) => ({
  ...(Boolean(dirtyFields.name) && { name: data.name }),
  ...(Boolean(dirtyFields.description) && { description: data.description }),
  ...(Boolean(dirtyFields.status) && { status: data.status }),
});
